import React from 'react';
import { getApiBase } from './apiBase';
import AiGearMark from './AiGearMark'; // solid blue gear + AI logo for the floating button
import ThinkingTimer from './ThinkingTimer';
import { AriaResizeGrip, ResizeEdges, renderEmph } from './ariaBoxKit';
import { canPopoutChat, popoutChat } from './popoutChat';

// AriaDock — an always-present Aria symbol pinned to the bottom-right of every
// screen. Tap it and a compact chat popup slides up so the owner can talk to
// Aria from anywhere in the app (Company view, Jobs, settings — wherever they
// are) without leaving what they're doing. Self-contained: its own message
// state + a direct POST to the same /api/ai/conversation brain the main Ask
// page uses, so replies come from Aria's local brain (no external LLM writes
// her words — TTS is intentionally skipped here, this is the text path).

function ownerId() {
  try { return (typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || ''; } catch (_) { return ''; }
}

const MicIcon = ({ on }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={on ? 2.6 : 2.2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 10a7 7 0 0 0 14 0M12 19v3" />
  </svg>
);

export default function AriaDock({ onAssist = null, popout = false } = {}) {
  const [open, setOpen] = React.useState(popout);
  // "+" in the dock = go into Assist mode (Aria uses the screen — navigate the
  // site, fill things in, etc.). Closes the dock first so the screen is clear.
  const goAssist = React.useCallback(() => {
    setOpen(false);
    try { if (onAssist) onAssist(); } catch (_) {}
  }, [onAssist]);
  const [messages, setMessages] = React.useState([]); // { role:'user'|'assistant', content }
  // Global hook: anything in the app can pop the NORMAL Aria box open with a
  // seeded question (e.g. the calendar's Get started button). Same box, same
  // look, just opened for the user with Aria already talking.
  React.useEffect(() => {
    const onOpen = (e) => {
      setOpen(true);
      const seed = e && e.detail && e.detail.say;
      if (seed) setMessages((m) => [...m, { role: 'assistant', content: seed }]);
    };
    window.addEventListener('aria:openDock', onOpen);
    return () => window.removeEventListener('aria:openDock', onOpen);
  }, []);
  const [input, setInput] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [recording, setRecording] = React.useState(false);
  const [micErr, setMicErr] = React.useState('');
  const scrollRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const mediaRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const chunksRef = React.useRef([]);

  // Draggable position — same behaviour as the support chat: drag by the header,
  // clamp to the viewport, persist per-user. Defaults to the bottom-right.
  const DOCK_W = 380;
  const DOCK_H = 560;
  const [pos, setPos] = React.useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('aria_dock_pos') || 'null');
      if (s && typeof s.left === 'number' && typeof s.top === 'number') return s;
    } catch (_) {}
    const left = (typeof window !== 'undefined' ? window.innerWidth : 1200) - DOCK_W - 24;
    const top = (typeof window !== 'undefined' ? window.innerHeight : 800) - DOCK_H - 24;
    return { left: Math.max(16, left), top: Math.max(16, top) };
  });

  const onHeaderMouseDown = (e) => {
    if (e.target.closest('.support-chat__close')) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, panelL: pos.left, panelT: pos.top };
    const boxEl = e.currentTarget.closest('.support-chat');
    // Tear-off: drag to the window edge and release to pop into its own window.
    // Works on both surfaces now (see popoutChat.js) - it used to be an Electron
    // check, so the identical drag armed in the app and did nothing on the web.
    const canTear = canPopoutChat(popout);
    let armed = false; const EDGE = 10;
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      setPos({
        left: Math.max(0, Math.min(window.innerWidth - 60, dragRef.current.panelL + dx)),
        top: Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.panelT + dy)),
      });
      if (canTear && boxEl) {
        armed = ev.clientX <= EDGE || ev.clientX >= window.innerWidth - EDGE || ev.clientY <= EDGE || ev.clientY >= window.innerHeight - EDGE;
        boxEl.classList.toggle('support-chat--tearing', armed);
      }
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragRef.current = null;
      if (boxEl) boxEl.classList.remove('support-chat--tearing');
      // A blocked popup must not swallow the gesture: if it did not open, leave
      // the box where he dragged it rather than closing it into nothing.
      if (armed && canTear && popoutChat()) setOpen(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Drag ANY edge or corner to resize. Writes the same --aria-box-w/h vars the
  // grip uses (one consistent size, persisted), and moves the top-left anchor
  // when the n/w edges are dragged so the opposite edge stays put.
  const startResize = (dir) => (e) => {
    const box = e.currentTarget.closest('.support-chat'); if (!box) return;
    e.preventDefault(); e.stopPropagation();
    const r = box.getBoundingClientRect();
    const B = { l: r.left, t: r.top, w: r.width, h: r.height, x: e.clientX, y: e.clientY };
    let last = { l: B.l, t: B.t };
    const move = (ev) => {
      const dx = ev.clientX - B.x, dy = ev.clientY - B.y;
      let l = B.l, t = B.t, w = B.w, h = B.h;
      if (dir.includes('e')) w = B.w + dx;
      if (dir.includes('w')) { w = B.w - dx; l = B.l + dx; }
      if (dir.includes('s')) h = B.h + dy;
      if (dir.includes('n')) { h = B.h - dy; t = B.t + dy; }
      if (w < 300) { if (dir.includes('w')) l = B.l + (B.w - 300); w = 300; }
      if (h < 360) { if (dir.includes('n')) t = B.t + (B.h - 360); h = 360; }
      w = Math.min(w, window.innerWidth - 24); h = Math.min(h, window.innerHeight - 40);
      last = { l, t };
      box.style.left = l + 'px'; box.style.top = t + 'px';
      document.documentElement.style.setProperty('--aria-box-w', w + 'px');
      document.documentElement.style.setProperty('--aria-box-h', h + 'px');
    };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      setPos({ left: last.l, top: last.t });
      try {
        localStorage.setItem('aria_box_w', document.documentElement.style.getPropertyValue('--aria-box-w'));
        localStorage.setItem('aria_box_h', document.documentElement.style.getPropertyValue('--aria-box-h'));
      } catch (_) {}
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // In the popped-out window, edge/corner drags resize the OS WINDOW itself
  // (via screen coords + setBounds), so it resizes smoothly on any monitor.
  const startWinResize = (dir) => async (e) => {
    if (!(window.electronAPI && window.electronAPI.dockGetBounds)) return;
    e.preventDefault(); e.stopPropagation();
    const b0 = await window.electronAPI.dockGetBounds(); if (!b0) return;
    // The visible card sits M px inside the transparent window (the glow margin).
    // Map the dragged edge to the cursor's ABSOLUTE screen position (offset by M)
    // so the card edge lands DEAD-ON under the cursor, not offset by where within
    // the handle it was grabbed.
    const M = 22;
    let raf = 0, pending = null;
    const flush = () => { raf = 0; if (pending) { try { window.electronAPI.dockSetBounds(pending); } catch (_) {} pending = null; } };
    const move = (ev) => {
      let x = b0.x, y = b0.y, width = b0.width, height = b0.height;
      const right0 = b0.x + b0.width, bottom0 = b0.y + b0.height;
      if (dir.includes('e')) width = (ev.screenX + M) - b0.x;
      if (dir.includes('s')) height = (ev.screenY + M) - b0.y;
      if (dir.includes('w')) { x = ev.screenX - M; width = right0 - x; }
      if (dir.includes('n')) { y = ev.screenY - M; height = bottom0 - y; }
      if (width < 320) { if (dir.includes('w')) x = right0 - 320; width = 320; }
      if (height < 420) { if (dir.includes('n')) y = bottom0 - 420; height = 420; }
      pending = { x, y, width, height };
      if (!raf) raf = requestAnimationFrame(flush);
    };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      if (raf) cancelAnimationFrame(raf);
      if (pending) { try { window.electronAPI.dockSetBounds(pending); } catch (_) {} }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  React.useEffect(() => {
    try { localStorage.setItem('aria_dock_pos', JSON.stringify(pos)); } catch (_) {}
  }, [pos]);

  // SNAP INTO VIEW on open. A saved position from a larger window (or one dragged
  // near an edge) could place the dock off-screen — then clicking the AI button
  // "opens" it where you can't see it (looks like nothing happened). Re-clamp to
  // the current viewport every time it opens so it's always visible.
  React.useEffect(() => {
    if (!open) return;
    setPos((p) => {
      const maxL = Math.max(8, (window.innerWidth || 1200) - DOCK_W - 12);
      const maxT = Math.max(8, (window.innerHeight || 800) - 140); // keep the header reachable on short windows
      const left = Math.min(Math.max(8, p.left), maxL);
      const top = Math.min(Math.max(8, p.top), maxT);
      return (left === p.left && top === p.top) ? p : { left, top };
    });
  }, [open]);

  // Keep the latest turn in view.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, open]);

  // Focus the input when the popup opens.
  React.useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => { try { inputRef.current.focus(); } catch (_) {} }, 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  const send = React.useCallback(async (override) => {
    const text = String(override != null ? override : input).trim();
    if (!text || sending) return;
    setInput('');
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setSending(true);
    try {
      const base = getApiBase();
      const url = `${String(base || '').replace(/\/$/, '')}/api/ai/conversation`;
      const history = next.slice(-6).map((m) => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: String(m.content || ''),
      }));
      const body = {
        initialQuery: text,
        prompt: text,
        language: 'en',
        inputMode: 'text',
        skipAudio: true,
        conversationHistory: history,
      };
      const uid = ownerId();
      if (uid) body.userId = uid;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      let data = null;
      try { const t = await res.text(); data = t ? JSON.parse(t) : {}; } catch (_) { data = {}; }

      if (!res.ok) {
        const msg = (data && (data.message || data.error)) ||
          (res.status === 402 ? "You've used your free allowance — open Subscription to keep chatting." : "Aria isn't available right now.");
        setMessages((m) => [...m, { role: 'assistant', content: msg, error: true }]);
        setSending(false);
        return;
      }
      const summary = data && Array.isArray(data.summaryMessages) && data.summaryMessages.length
        ? data.summaryMessages.join(' ').trim() : '';
      let reply = (data && (data.reply || data.question || data.reasoning || summary)) || '';
      reply = String(reply || '').trim() || "I'm here — say that another way?";
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch (_) {
      setMessages((m) => [...m, { role: 'assistant', content: "Can't reach Aria right now. Check your connection and try again.", error: true }]);
    }
    setSending(false);
  }, [input, sending, messages]);

  // ── Mic: record → transcribe (same path as the main app + support box) → send ──
  const transcribe = React.useCallback(async (blob, mime) => {
    const base64 = await new Promise((resolve, reject) => { const r = new FileReader(); r.onloadend = () => resolve(String(r.result || '').split(',')[1] || ''); r.onerror = () => reject(new Error('read')); r.readAsDataURL(blob); });
    if (!base64) return '';
    const billing = {}; const uid = ownerId(); if (uid) billing.userId = uid;
    if (typeof window !== 'undefined' && window.electronAPI?.transcribeAudio) {
      const ipc = await window.electronAPI.transcribeAudio({ audio: base64, mime, ...billing });
      const data = (ipc && ipc.data) || {};
      return (ipc && ipc.ok && data.text) ? String(data.text).trim() : '';
    }
    const base = String(getApiBase() || '').replace(/\/$/, '');
    const r = await fetch(`${base}/api/speech/transcribe`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ audio: base64, mime, ...billing }) });
    const data = await r.json().catch(() => ({}));
    return (r.ok && data && data.text) ? String(data.text).trim() : '';
  }, []);

  const toggleMic = React.useCallback(async () => {
    if (recording) { try { mediaRef.current && mediaRef.current.stop(); } catch (_) {} return; }
    setMicErr('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const supported = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
      const mime = supported && MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { (streamRef.current?.getTracks() || []).forEach((t) => t.stop()); } catch (_) {}
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (!blob.size) return;
        try { const text = await transcribe(blob, mr.mimeType || 'audio/webm'); if (text) await send(text); }
        catch (_) { setMicErr('Couldn’t hear that — try again or type.'); }
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch (_) { setMicErr('Mic not available — you can type instead.'); }
  }, [recording, transcribe, send]);

  // Stop the mic if the dock closes/unmounts mid-recording.
  React.useEffect(() => () => { try { (streamRef.current?.getTracks() || []).forEach((t) => t.stop()); } catch (_) {} }, []);
  React.useEffect(() => { if (!open && recording) { try { mediaRef.current && mediaRef.current.stop(); } catch (_) {} } }, [open, recording]);

  return (
    <>
      {open && (
        /* Identical to the support chat (.support-chat*), just anchored above
           the floating button. Same header, greeting bubble, bubbles, and gold
           pill input + round send. */
        <div
          className="support-chat support-chat--dock"
          role="dialog"
          aria-label="Talk to Aria"
          style={popout ? { position: 'fixed', top: 22, left: 22, right: 'auto', bottom: 'auto', width: 'calc(100% - 44px)', height: 'calc(100% - 44px)', maxWidth: 'none', maxHeight: 'none', minWidth: 0, borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 0 16px 1px rgba(0,150,255,0.45)' } : { left: `${pos.left}px`, top: `${pos.top}px`, right: 'auto', bottom: 'auto' }}
        >
          <ResizeEdges start={popout ? startWinResize : startResize} />
          <div
            className="support-chat__header support-chat__header--centered"
            onMouseDown={popout ? undefined : onHeaderMouseDown}
            style={{ cursor: popout ? 'default' : 'grab', userSelect: 'none', WebkitAppRegion: popout ? 'drag' : 'no-drag' }}
          >
            <div className="support-chat__script">Aria</div>
            <div className="support-chat__header-actions" style={{ WebkitAppRegion: 'no-drag' }}>
              <button type="button" className="support-chat__close" aria-label="Close"
                onClick={() => { if (popout) { try { window.close(); } catch (_) {} } else { setOpen(false); } }}>×</button>
            </div>
          </div>

          <div className="support-chat__body" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="support-chat__bubble support-chat__bubble--assistant">
                Hey — I&rsquo;m Aria. Ask me anything: schedule a job, draft a quote, check today&rsquo;s numbers.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`support-chat__bubble support-chat__bubble--${m.role === 'user' ? 'user' : 'assistant'}`}>
                {m.role === 'user' ? m.content : renderEmph(m.content)}
              </div>
            ))}
            {sending && (
              <div className="support-chat__bubble support-chat__bubble--assistant" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span className="support-chat__typing" style={{ display: 'inline-flex', gap: 3 }}><span /><span /><span /></span>
                <ThinkingTimer label="Thinking" />
              </div>
            )}
            {micErr && <div className="support-chat__bubble support-chat__bubble--assistant" style={{ opacity: 0.8 }}>{micErr}</div>}
          </div>

          {!popout && <AriaResizeGrip />}
          <form className="support-chat__input-row" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <button
              type="button"
              onClick={goAssist}
              aria-label="Assist — let Aria use your screen"
              title="Assist — let Aria help on your screen (navigate the site, fill things in)"
              style={{ flex: 'none', width: 38, height: 38, borderRadius: 999, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2,#888)', background: 'rgba(127,127,127,0.16)' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            </button>
            {/* Apple-style: mic is the default action; it hides once there's text
                and the send arrow takes its place. Stays visible while recording. */}
            {(!input.trim() || recording) && (
            <button
              type="button"
              onClick={toggleMic}
              aria-label={recording ? 'Stop recording' : 'Talk to Aria'}
              title={recording ? 'Listening… tap to stop' : 'Talk to Aria'}
              style={{ flex: 'none', width: 38, height: 38, borderRadius: 999, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: recording ? '#fff' : 'var(--text-2,#888)', background: recording ? '#ff453a' : 'rgba(127,127,127,0.16)', boxShadow: recording ? '0 0 0 4px rgba(255,69,58,0.25)' : 'none', transition: 'background .15s' }}
            >
              <MicIcon on={recording} />
            </button>
            )}
            <input
              ref={inputRef}
              type="text"
              placeholder={recording ? 'Listening…' : 'Ask Aria, or tap the mic to talk…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            {input.trim() && (
            <button type="submit" disabled={sending} aria-label="Send">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></svg>
            </button>
            )}
          </form>
        </div>
      )}

      {!open && (
        <button
          type="button"
          className="aria-dock-fab"
          onClick={() => {
            // The ONE movable window, on both surfaces (see popoutChat.js).
            // The web arm used to fall straight through to the in-page box on
            // the grounds that there are "no OS windows there" - there are, the
            // popout is a route and window.open reaches it. Falls back to the
            // in-page box only if the popup is actually blocked.
            if (canPopoutChat(popout) && popoutChat()) return;
            setOpen(true);
          }}
          style={{ display: popout ? 'none' : undefined }}
          aria-label="Talk to Aria"
          title="Talk to Aria"
        >
          <span className="aria-dock-fab__ask">Need help?</span>
        </button>
      )}
    </>
  );
}
